import { type ReactNode } from "react";
import { Command } from "cmdk";
import { Search } from "lucide-react";
import { Kbd } from "@/components/ui/kbd";
import { zCommand } from "@/lib/z";
import { cn } from "@/lib/cn";

export interface CommandItem {
  /** Stable id (also used as the cmdk value for filtering). */
  id: string;
  label: ReactNode;
  /** Searchable keywords beyond the label text. */
  keywords?: string[];
  icon?: ReactNode;
  shortcut?: string[];
  onSelect: () => void;
}

export interface CommandGroup {
  heading: ReactNode;
  items: CommandItem[];
}

export interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Grouped items (Navigate / Actions / Search) supplied by the host page. */
  groups: CommandGroup[];
  placeholder?: string;
  emptyText?: ReactNode;
}

/**
 * CommandPalette — Cmd/Ctrl+K launcher built on cmdk (DESIGN-SYSTEM §5, §6.8).
 * Items are passed in by props; pages mount their own Navigate/Actions/Search
 * groups. Pair with {@link useCommandPaletteShortcut} to toggle on Cmd/Ctrl+K.
 */
export function CommandPalette({
  open,
  onOpenChange,
  groups,
  placeholder = "Команда или поиск…",
  emptyText = "Ничего не найдено.",
}: CommandPaletteProps) {
  return (
    <Command.Dialog
      open={open}
      onOpenChange={onOpenChange}
      label="Командная палитра"
      shouldFilter
      className={cn(
        "fixed left-1/2 top-[20vh] w-[calc(100vw-2rem)] max-w-xl -translate-x-1/2",
        "overflow-hidden rounded-xl border border-border bg-bg-secondary text-fg-primary shadow-xl",
      )}
      // cmdk renders its own Radix dialog overlay; lift it above everything.
      style={{ zIndex: zCommand }}
    >
      <div className="flex items-center gap-2 border-b border-border px-3">
        <Search size={16} strokeWidth={2} aria-hidden="true" className="shrink-0 text-fg-muted" />
        <Command.Input
          placeholder={placeholder}
          className="h-12 w-full bg-transparent text-sm text-fg-primary outline-none placeholder:text-fg-muted"
        />
      </div>
      <Command.List className="max-h-[50vh] overflow-y-auto p-2">
        <Command.Empty className="py-6 text-center text-sm text-fg-muted">
          {emptyText}
        </Command.Empty>
        {groups.map((group, gi) => (
          <Command.Group
            key={gi}
            heading={group.heading}
            className="px-1 pb-1 text-xs font-medium text-fg-muted [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5"
          >
            {group.items.map((item) => (
              <Command.Item
                key={item.id}
                value={`${item.id} ${item.keywords?.join(" ") ?? ""}`}
                onSelect={() => {
                  item.onSelect();
                  onOpenChange(false);
                }}
                className={cn(
                  "flex cursor-pointer items-center gap-2 rounded-md px-2 py-2 text-sm text-fg-primary",
                  "data-[selected=true]:bg-bg-elevated data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50",
                )}
              >
                {item.icon && (
                  <span className="flex h-4 w-4 shrink-0 items-center justify-center text-fg-muted">
                    {item.icon}
                  </span>
                )}
                <span className="flex-1 truncate">{item.label}</span>
                {item.shortcut && (
                  <span className="ml-auto flex items-center gap-1">
                    {item.shortcut.map((k, ki) => (
                      <Kbd key={ki}>{k}</Kbd>
                    ))}
                  </span>
                )}
              </Command.Item>
            ))}
          </Command.Group>
        ))}
      </Command.List>
    </Command.Dialog>
  );
}
CommandPalette.displayName = "CommandPalette";

// The Cmd/Ctrl+K toggle hook lives in `@/lib/use-command-palette`
// (`useCommandPaletteShortcut`) — kept out of this component file so the
// react-refresh lint rule (components-only exports) stays satisfied.
